// Client-side image downscaling for fast, phone-friendly uploads.
// Shrinks photos to a web/mobile-friendly size and converts HEIC/PNG -> JPEG,
// so big phone photos never overwhelm mobile browser memory (which causes reloads).

const MAX_EDGE = 1920;   // medium/web quality — plenty for websites & mobile
const QUALITY = 0.72;    // good size/quality balance
const THUMB_EDGE = 320;  // tiny preview to keep memory low

async function loadBitmap(file) {
  if (window.createImageBitmap) {
    try { return await createImageBitmap(file); } catch (_) { /* fall through */ }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function dims(bmp) {
  return { w: bmp.width || bmp.naturalWidth, h: bmp.height || bmp.naturalHeight };
}

// Returns a compressed JPEG File (or the original if compression isn't possible).
export async function resizeImageForUpload(file, maxEdge = MAX_EDGE, quality = QUALITY) {
  if (!file || !(file.type?.startsWith("image/") || /\.(heic|heif)$/i.test(file.name))) return file;
  let bmp;
  try {
    bmp = await loadBitmap(file);
  } catch (_) {
    return file; // browser can't decode (rare) — let the backend handle the original
  }
  try {
    const { w, h } = dims(bmp);
    if (!w || !h) return file;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    // Already small AND already a jpeg -> no need to recompress.
    if (scale >= 1 && /jpe?g/i.test(file.type)) { bmp.close?.(); return file; }
    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = nw; canvas.height = nh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bmp, 0, 0, nw, nh);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    canvas.width = 0; canvas.height = 0;
    if (!blob) return file;
    const name = (file.name || "photo").replace(/\.(heic|heif|png|webp|gif|jpe?g)$/i, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch (_) {
    bmp?.close?.();
    return file;
  }
}

// Small JPEG data URL for a preview thumbnail (keeps memory low on phones).
export async function makeThumb(file, size = THUMB_EDGE) {
  let bmp;
  try {
    bmp = await loadBitmap(file);
    const { w, h } = dims(bmp);
    const scale = Math.min(1, size / Math.max(w, h));
    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = nw; canvas.height = nh;
    canvas.getContext("2d").drawImage(bmp, 0, 0, nw, nh);
    bmp.close?.();
    const url = canvas.toDataURL("image/jpeg", 0.6);
    canvas.width = 0; canvas.height = 0;
    return url;
  } catch (_) {
    bmp?.close?.();
    return null;
  }
}
